"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.isNone = exports.DefaultSerializer = exports.EngageActionPerformer = void 0;
const EngageLogger_1 = require("./EngageLogger");
const EngageStats_1 = require("./EngageStats");
const track_1 = require("./track");
const isDestinationActionService_1 = require("./isDestinationActionService");
const ResponseError_1 = require("./ResponseError");
const actions_core_1 = require("@segment/actions-core");
const IntegrationErrorWrapper_1 = require("./IntegrationErrorWrapper");
const operationTracking_1 = require("./operationTracking");
const truncate_1 = __importDefault(require("lodash/truncate"));
const isRetryableError_1 = require("./isRetryableError");
const getOrCatch_1 = require("./getOrCatch");
const getOrRetry_1 = require("./getOrRetry");
class EngageActionPerformer {
    constructor(requestClient, executeInput) {
        this.requestClient = requestClient;
        this.executeInput = executeInput;
        this.payload = executeInput.payload;
        this.settings = executeInput.settings;
        this.engageDestinationCache = executeInput.engageDestinationCache;
        this.logger = new EngageLogger_1.EngageLogger(this);
        this.statsClient = new EngageStats_1.EngageStats(this);
    }
    async perform() {
        await this.beforePerform?.();
        this.statsIncr('perform_attempt');
        return this.doPerform();
    }
    async request(url, options) {
        const op = this.currentOperation;
        op?.onFinally.push(() => {
            const respError = op?.error;
            if (respError) {
                const errorDetails = (0, ResponseError_1.getErrorDetails)(respError);
                const msgLowercare = errorDetails?.message?.toLowerCase();
                const isTimeoutError = msgLowercare?.includes('timeout') ||
                    msgLowercare?.includes('timedout') ||
                    msgLowercare?.includes('exceeded the deadline') ||
                    errorDetails?.code?.toLowerCase().includes('etimedout');
                if (isTimeoutError) {
                    const status = errorDetails?.status ?? respError.status ?? 408;
                    respError.status = status;
                    if (errorDetails)
                        errorDetails.status = status;
                    const errorCode = errorDetails?.code ?? respError.code ?? 'etimedout';
                    respError.code = errorCode;
                    if (errorDetails)
                        errorDetails.code = errorCode;
                    respError.retry = true;
                }
                if (errorDetails?.code)
                    op.tags.push(`response_code:${errorDetails.code}`);
                if (errorDetails?.status)
                    op.tags.push(`response_status:${errorDetails.status}`);
                if (this.onResponse)
                    try {
                        this.onResponse({ error: respError, operation: op });
                    }
                    catch (e) {
                        op.logs.push(`Error in onResponse: ${e}`);
                    }
            }
            else {
                const resp = op?.result;
                if (resp && resp.status)
                    op.tags.push(`response_status:${resp.status}`);
                if (this.onResponse)
                    try {
                        this.onResponse({ response: resp, operation: op });
                    }
                    catch (e) {
                        op.logs.push(`Error in onResponse: ${e}`);
                    }
            }
        });
        return await this.requestClient(url, {
            timeout: this.isRequestTimeoutExtended() ? 60000 * 5 : 30000,
            ...options
        });
    }
    redactPii(pii) {
        if (!pii) {
            return pii;
        }
        if (pii.length <= 8) {
            return '***';
        }
        return pii.substring(0, 3) + '***' + pii.substring(pii.length - 3);
    }
    isFeatureActive(featureName, getDefault) {
        if ((0, isDestinationActionService_1.isDestinationActionService)())
            return true;
        if (!this.executeInput.features || !(featureName in this.executeInput.features))
            return getDefault?.();
        return this.executeInput.features[featureName];
    }
    logInfo(msg, metadata) {
        this.logger.logInfo(msg, metadata);
    }
    logError(msg, metadata) {
        this.logger.logError(msg, metadata);
    }
    logWarn(msg, metadata) {
        this.logger.logWarn(msg, metadata);
    }
    logOnError(logMessage) {
        const op = this.currentOperation;
        op?.onFinally.push(() => {
            if (op.error) {
                const msg = typeof logMessage === 'function' ? logMessage(op) : logMessage;
                op.logs.push(msg);
            }
        });
    }
    statsIncr(metric, value, tags) {
        this.statsClient.stats({ method: 'incr', metric, value, tags });
    }
    statsHistogram(metric, value, tags) {
        this.statsClient.stats({ method: 'histogram', metric, value, tags });
    }
    statsSet(metric, value, tags) {
        this.statsClient.stats({ method: 'set', metric, value, tags });
    }
    get logDetails() {
        return this.logger.logDetails;
    }
    get tags() {
        return this.statsClient.tags;
    }
    rethrowIntegrationError(error, getWrapper) {
        throw IntegrationErrorWrapper_1.IntegrationErrorWrapper.wrap(error, getWrapper, this.currentOperation);
    }
    throwRetryableError(message, code = 'ETIMEDOUT') {
        const error = new actions_core_1.IntegrationError(message, code, 500);
        error.retry = true;
        throw error;
    }
    createStepLogger(args) {
        const { tags = {}, logs = {} } = args;
        const messageId = this.executeInput['rawData']?.messageId;
        logs.messageId = messageId;
        const operation = operationTracking_1.OperationDecorator.getOperationName(this.currentOperation) ||
            this.currentOperation?.func.name ||
            '';
        const appendStepDetails = (stepname, details) => {
            tags['step_' + stepname] = true;
            if (details?.tags) {
                for (const [key, value] of Object.entries(details.tags))
                    tags[stepname + '_' + key] = value;
            }
            if (details?.logs) {
                for (const [key, value] of Object.entries(details.logs))
                    logs[stepname + '_' + key] = value instanceof Error ? (0, ResponseError_1.getErrorDetails)(value) : value;
            }
        };
        const write = (stepName, details, durationMs) => {
            appendStepDetails(stepName, details);
            tags.last_step = stepName;
            this.statsIncr(`${operation}_${stepName}`, 1, tags);
            const hasError = !!(details?.tags?.error || details?.logs?.error);
            const level = details?.level ? details.level : hasError ? 'warn' : 'info';
            switch (level) {
                case 'error':
                    this.logError(`${operation}_${stepName}`, logs);
                    break;
                case 'warn':
                    this.logWarn(`${operation}_${stepName}`, logs);
                    break;
                default:
                    this.logInfo(`${operation}_${stepName}`, logs);
                    break;
            }
            if (durationMs !== undefined) {
                this.statsHistogram(`${operation}_${stepName}.duration`, durationMs, tags);
            }
        };
        const track = (stepName, fn) => {
            const startStep = `${stepName}_starting`;
            write(startStep);
            const stepDetails = { logs: {}, tags: {} };
            let fnRes = undefined;
            let error = undefined;
            let isPromise = false;
            const startTime = Date.now();
            const onFinally = () => {
                const durationMs = Date.now() - startTime;
                const valueOrError = fnRes instanceof Object && ('error' in fnRes || 'value' in fnRes) ? fnRes : undefined;
                if (valueOrError)
                    fnRes = valueOrError.value;
                error = error || valueOrError?.error;
                stepDetails.tags.error = !!error;
                if (error) {
                    stepDetails.logs.error = error ? (0, ResponseError_1.getErrorDetails)(error) : undefined;
                }
                else {
                    const valueStr = (0, getOrCatch_1.getOrCatch)(() => (isNone(fnRes) ? '(none)' : fnRes instanceof Object ? JSON.stringify(fnRes) : `${fnRes}`).substring(0, 100));
                    stepDetails.logs.value = valueStr.error ? `<error> ${valueStr.error.message}` : `${valueStr.value}`;
                }
                write(`${stepName}_finished`, stepDetails, durationMs);
            };
            const onCatch = (e) => {
                error = e;
                throw e;
            };
            try {
                const result = fn(stepDetails);
                if (result instanceof Promise) {
                    isPromise = true;
                    return result
                        .then((res) => {
                        return (fnRes = res);
                    })
                        .catch(onCatch)
                        .finally(onFinally);
                }
                return result;
            }
            catch (e) {
                onCatch(e);
            }
            finally {
                if (!isPromise) {
                    onFinally();
                }
            }
        };
        this.currentOperation?.onFinally.push(() => {
            const tagsArray = this.statsClient.tagsMapToArray(tags);
            this.currentOperation?.tags.push(...tagsArray);
        });
        return {
            logs,
            tags,
            track,
            write
        };
    }
    async getOrAddCache(key, createValue, options) {
        const cache_group = options.cacheGroup || this.currentOperation?.parent?.func.name || '';
        const step = this.createStepLogger({
            tags: {
                cache_group: cache_group,
                withLock: !!options.lockOptions,
                last_step: 'init'
            },
            logs: { key }
        });
        if (!this.engageDestinationCache)
            return step.track('no_cache', () => createValue());
        const cache = this.engageDestinationCache;
        const serializer = options.serializer || exports.DefaultSerializer;
        const cacheRead = await step.track('cache_reading', () => (0, getOrCatch_1.getOrCatch)(() => cache.getByKey(key)));
        if (cacheRead.error) {
            this.throwRetryableError('Error reading cache: ' + cacheRead.error.message);
        }
        if (isNone(cacheRead.value) && options.lockOptions) {
            if (!options.lockOptions.cacheGroup)
                options.lockOptions.cacheGroup = cache_group;
            return step.track('cache_locking', () => this.withDistributedLock(`cache:${key}`, () => this.getOrAddCache(key, createValue, { ...options, lockOptions: undefined }), options.lockOptions));
        }
        if (!isNone(cacheRead.value)) {
            const { value: parsedCache, error: parsingError } = step.track('cache_parse', () => (0, getOrCatch_1.getOrCatch)(() => serializer.parse(cacheRead.value)));
            if (!parsingError)
                if (isNone(parsedCache)) {
                    step.write('cache_ignored');
                }
                else {
                    step.write('cache_hit', { tags: { cache_hit: true, cached_error: !!parsedCache.error } });
                    if (parsedCache.error) {
                        throw parsedCache.error;
                    }
                    return parsedCache.value;
                }
        }
        step.write('cache_miss');
        const { value: result, error: resultError } = await step.track('cache_create_value', () => (0, getOrCatch_1.getOrCatch)(() => createValue()));
        const stringified = step.track('cache_stringified', () => (0, getOrCatch_1.getOrCatch)(() => serializer.stringify(resultError ? { error: resultError } : { value: result })));
        if (!stringified.error)
            if (!isNone(stringified.value)) {
                const { error: cacheSavingError, value: cacheSavingResult } = await step.track('cache_save', () => (0, getOrRetry_1.getOrRetry)(() => cache.setByKey(key, stringified.value, options.expiryInSeconds), options.saveRetry || {
                    attempts: 5,
                    retryIntervalMs: (0, getOrRetry_1.backoffRetryPolicy)(10000, 3),
                    onFailedAttempt: (error, attemptCount) => {
                        step.write('cache_save_failed_attempt', { logs: { attemptCount, error: (0, ResponseError_1.getErrorDetails)(error) } });
                    }
                }));
                if (cacheSavingError || !cacheSavingResult) {
                    options?.onSaveFailed?.(cacheSavingError);
                }
            }
            else {
                step.write('cache_save_skipped');
            }
        if (resultError)
            throw resultError;
        else
            return result;
    }
    async withDistributedLock(key, createValue, options) {
        const cache_group = options.cacheGroup || this.currentOperation?.parent?.func.name || '';
        const step = this.createStepLogger({
            logs: {
                key
            },
            tags: {
                cache_group
            }
        });
        if (!this.engageDestinationCache) {
            return await createValue();
        }
        const cache = this.engageDestinationCache;
        const lockKey = `lock:${key}`;
        const acquireLock = async () => {
            const startTime = Date.now();
            while (Date.now() - startTime < options.acquireLockMaxWaitTimeMs) {
                step.write('cache_lock_attempt');
                if (await step.track('redis_setNX', () => cache.setByKeyNX(lockKey, 'locked', options.lockMaxTimeMs / 1000))) {
                    step.write('cache_lock_acquired', { tags: { lock_acquired: true } });
                    this.statsHistogram('cache_lock_waiting_time', Date.now() - startTime, step.tags);
                    return {
                        release: () => step.track('redis_delByKey', () => cache.delByKey(lockKey))
                    };
                }
                step.write('cache_lock_waiting');
                await new Promise((resolve) => setTimeout(resolve, options.acquireLockRetryIntervalMs || 500));
            }
            step.write('cache_lock_timeout');
        };
        const { value: lock, error: redisError } = await step.track('cache_acquire_lock', () => (0, getOrCatch_1.getOrCatch)(() => acquireLock()));
        if (redisError) {
            this.throwRetryableError('Error acquiring lock: ' + redisError.message);
        }
        else if (!lock?.release) {
            step.write('cache_lock_timeout_exit');
            this.throwRetryableError('Timeout while acquiring lock');
        }
        else {
            const { value: createdValue, error: createValueError } = await step.track('cache_lock_create', () => (0, getOrCatch_1.getOrCatch)(() => createValue()));
            await step.track('cache_lock_release', () => (0, getOrCatch_1.getOrCatch)(() => lock.release()));
            if (createValueError)
                throw createValueError;
            return createdValue;
        }
    }
    isLockExpirationExtended() {
        return this.isFeatureActive('engage-messaging-lock-expiration-extended', () => false);
    }
    isRequestTimeoutExtended() {
        return this.isFeatureActive('engage-messaging-request-timeout-extended', () => false);
    }
}
__decorate([
    (0, track_1.track)()
], EngageActionPerformer.prototype, "perform", null);
__decorate([
    (0, track_1.track)({
        onTry: addUrlToLog,
        onFinally: addUrlToLog
    })
], EngageActionPerformer.prototype, "request", null);
__decorate([
    (0, track_1.track)()
], EngageActionPerformer.prototype, "getOrAddCache", null);
__decorate([
    (0, track_1.track)()
], EngageActionPerformer.prototype, "withDistributedLock", null);
exports.EngageActionPerformer = EngageActionPerformer;
function addUrlToLog(ctx) {
    const ctxFull = ctx;
    const argUrl = ctx.funcArgs?.[0];
    if (ctxFull.logs && argUrl) {
        ctxFull.logs.push((0, truncate_1.default)(argUrl, { length: 70 }));
    }
}
exports.DefaultSerializer = {
    stringify: (valueOrError) => {
        let cacheObj = undefined;
        if (valueOrError.error && !(0, isRetryableError_1.isRetryableError)(valueOrError.error)) {
            const errorDetails = (0, ResponseError_1.getErrorDetails)(valueOrError.error);
            if (errorDetails?.status) {
                cacheObj = { error: errorDetails };
            }
        }
        else if (valueOrError.value !== undefined) {
            cacheObj = { value: valueOrError.value };
        }
        return cacheObj ? JSON.stringify(cacheObj) : undefined;
    },
    parse: (cachedValue) => {
        const parsed = JSON.parse(cachedValue);
        if (parsed.error) {
            const error = new actions_core_1.IntegrationError(parsed.error.message, parsed.error.code, parsed.error.status || 400);
            error.retry = false;
            return { error };
        }
        else {
            return { value: parsed.value };
        }
    }
};
function isNone(cacheValue) {
    return cacheValue === undefined || cacheValue === null;
}
exports.isNone = isNone;
//# sourceMappingURL=EngageActionPerformer.js.map